import { useTranslation } from "react-i18next";
import { ExternalLink, RefreshCw } from "lucide-react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { InboxConversationPane, InboxDialogList, useInboxList } from "@/components/inbox";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Navigate the hand-rolled App.tsx router to the full inbox page. */
function goToFullInbox() {
  window.history.pushState(null, "", "/inbox");
  window.dispatchEvent(new PopStateEvent("popstate"));
}

/**
 * Slide-in inbox panel — the canonical quick-answer surface (IA §1.2).
 * Mounted once at the app shell (CompanyShell) so the operator can dip in/out
 * of conversations without leaving the current page. The full /inbox page is
 * for triage; this drawer links to it via "Open full inbox".
 *
 * Built on the shared Drawer primitive (Radix Dialog → focus-trap, Esc,
 * scroll-lock, backdrop). Props contract (`open`/`onClose`) is unchanged —
 * CompanyShell mounts it.
 *
 * The list and the conversation are the ones /inbox uses (useInboxList,
 * InboxDialogList, InboxConversationPane); only the layout is the drawer's own.
 */
export function InboxDrawer({ open, onClose }: Props) {
  const { t } = useTranslation();
  // The drawer stays mounted at the shell: closed, it selects nothing and does
  // not poll Telegram.
  const list = useInboxList({ active: open });
  const { selected } = list;

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent size="lg" className="bg-bg-primary p-0">
        <DrawerHeader className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              {/* The short name: the long one did not fit beside the rest. */}
              <DrawerTitle className="text-lg">{t("nav.inbox")}</DrawerTitle>
              <Switch
                checked={list.unreadOnly}
                onCheckedChange={(v) => list.setUnreadOnly(!!v)}
                label={t("inbox.unread_only")}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                size="sm"
                variant="link"
                onClick={() => {
                  onClose();
                  goToFullInbox();
                }}
                iconRight={<ExternalLink size={14} strokeWidth={2} aria-hidden="true" />}
              >
                {t("inbox.open_full")}
              </Button>
              {/* The icon alone: with its words the header ran into a second line. */}
              <Button
                size="sm"
                variant="outline"
                className="w-8 px-0"
                onClick={list.refresh}
                loading={list.refreshing}
                aria-label={t("inbox.refresh_now")}
                title={t("inbox.refresh_now")}
                iconLeft={<RefreshCw size={15} strokeWidth={2} aria-hidden="true" />}
              />
            </div>
          </div>
        </DrawerHeader>

        <div className="grid min-h-0 flex-1 grid-cols-[280px_minmax(0,1fr)] overflow-hidden">
          {/* Dialog list */}
          <div className="flex min-h-0 flex-col border-r border-border bg-bg-secondary">
            <InboxDialogList list={list} compact />
          </div>

          {/* Conversation */}
          <div className="flex min-h-0 min-w-0 flex-col">
            {selected ? (
              <InboxConversationPane
                dialog={selected}
                onArchive={() => list.archiveSelected()}
                onNavigate={onClose}
              />
            ) : (
              <div className="flex flex-1 items-center justify-center p-6 text-sm text-fg-muted">
                {t("inbox.pick_dialog")}
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
